'use client'

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useEditor, EditorContent } from '@tiptap/react'
import { BubbleMenu } from '@tiptap/react/menus'
import debounce from 'lodash.debounce'
import { cn } from '@/lib/utils'
import { useSemanticRowList } from '@/lib/notebook/core/context/semantic-row-list-context'
import {
  clearRowFocusMeta,
  registerRowFocusMeta,
} from '@/lib/notebook/models/semantic-row-paste'
import { createNotebookTiptapExtensions } from './notebook-tiptap-extensions'
import { stripHtmlToPlainText } from '@/lib/notebook/text/plain-text-value'
import { NotebookBubbleToolbar } from './NotebookBubbleToolbar'
import { registerNotebookEditor } from '@/lib/notebook/core/tiptap-lifecycle'
import { nbLog, nbWarn, scanNotebookDom } from '@/lib/notebook/core/notebook-debug'
import { getSafeEditorDom, getSafeEditorView } from './notebook-tiptap-safe'

function escapeHtml(text) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function toInitialHtml(value, richText) {
  const raw = value ?? ''
  if (!raw.trim()) return '<p></p>'
  if (richText && raw.trim().startsWith('<')) return raw
  return `<p>${escapeHtml(raw)}</p>`
}

function plainTextFromEditor(editor, richText) {
  return richText ? editor.getHTML() : editor.getText()
}

function clampPlainText(text, maxLength) {
  if (!maxLength || maxLength <= 0) return text
  const chars = [...(text ?? '')]
  if (chars.length <= maxLength) return chars.join('')
  return chars.slice(0, maxLength).join('')
}

function isFocusInBubbleToolbar(toolbarRoot) {
  const active = document.activeElement
  return Boolean(active && toolbarRoot?.contains(active))
}

function selectionAtStart(view) {
  const { $from } = view.state.selection
  return $from.parentOffset === 0
}

function selectionAtEnd(view) {
  const { $from } = view.state.selection
  return $from.parentOffset === $from.parent.content.size
}

function useNotebookBubbleMenuDismiss(editor, toolbarRootRef, fieldRef) {
  useEffect(() => {
    if (!editor || editor.isDestroyed) return

    const collapseSelection = () => {
      const { from } = editor.state.selection
      editor.commands.setTextSelection(from)
      editor.commands.blur()
    }

    const onDocumentKeyDown = (event) => {
      if (event.key !== 'Escape') return
      if (!editor.isFocused && !isFocusInBubbleToolbar(toolbarRootRef.current)) {
        return
      }
      event.preventDefault()
      collapseSelection()
    }

    const onDocumentPointerDown = (event) => {
      const target = event.target
      if (!(target instanceof Node)) return
      if (fieldRef.current?.contains(target)) return
      if (toolbarRootRef.current?.contains(target)) return
      if (!editor.isFocused && !isFocusInBubbleToolbar(toolbarRootRef.current)) {
        return
      }
      collapseSelection()
    }

    document.addEventListener('keydown', onDocumentKeyDown, true)
    document.addEventListener('mousedown', onDocumentPointerDown, true)
    document.addEventListener('touchstart', onDocumentPointerDown, true)
    return () => {
      document.removeEventListener('keydown', onDocumentKeyDown, true)
      document.removeEventListener('mousedown', onDocumentPointerDown, true)
      document.removeEventListener('touchstart', onDocumentPointerDown, true)
    }
  }, [editor, toolbarRootRef, fieldRef])
}

/**
 * Shared inline Tiptap field — navigation-safe destroy via editorRef lifecycle.
 * `instanceKey` must be stable per logical field within a route session.
 */
/** TEMP TIPTAP ISOLATION — live editor path (ProseMirror + bubble menu). */
function NotebookTiptapFieldLive({
  instanceKey = 'default',
  sessionEpoch = 0,
  value = '',
  onSave,
  locked = false,
  className = '',
  style,
  richText = false,
  singleLine = false,
  inline = false,
  rowIndex = null,
  rowId = null,
  rowField = 'text',
  isolatePasteBubble = false,
  maxLength,
  onBackspaceWhenEmpty,
  autoFocus = false,
  onDeactivate,
  bubbleToolbar = true,
}) {
  const toolbarRootRef = useRef(null)
  const fieldRef = useRef(null)
  const editorRef = useRef(null)
  const maxLengthRef = useRef(maxLength)
  maxLengthRef.current = maxLength
  const onSaveRef = useRef(onSave)
  const composingRef = useRef(false)
  const semanticRows = useSemanticRowList()

  onSaveRef.current = onSave

  const isSemanticRow = Boolean(rowId)
  const rowNav =
    isSemanticRow && semanticRows && rowIndex != null
      ? semanticRows.getRowNav(rowIndex)
      : null
  const rowNavRef = useRef(rowNav)
  rowNavRef.current = rowNav

  const registerFocus = useCallback(() => {
    if (!isSemanticRow || !rowId) return
    registerRowFocusMeta({ rowId, field: rowField })
  }, [isSemanticRow, rowId, rowField])

  const debouncedSave = useRef(
    debounce((payload) => {
      if (composingRef.current) return
      onSaveRef.current?.(payload)
    }, 400),
  ).current

  const flushSave = useCallback(
    (ed) => {
      if (!ed || ed.isDestroyed || composingRef.current) return
      let payload = richText
        ? ed.getHTML()
        : stripHtmlToPlainText(ed.getText())
      if (maxLength && !richText) {
        payload = clampPlainText(payload, maxLength)
        if (ed.getText().length > maxLength) {
          ed.commands.setContent(toInitialHtml(payload, false), { emitUpdate: false })
        }
      }
      debouncedSave.cancel()
      onSaveRef.current?.(payload)
    },
    [debouncedSave, richText, maxLength],
  )

  const editor = useEditor(
    {
      extensions: createNotebookTiptapExtensions(),
      content: toInitialHtml(value, richText),
      editable: !locked,
      immediatelyRender: false,
      editorProps: {
        attributes: {
          class: 'nb-tiptap-editor__surface',
          spellcheck: 'true',
        },
        handleTextInput: (view, from, to, text) => {
          const cap = maxLengthRef.current
          if (!cap || richText) return false
          const current = view.state.doc.textContent
          const selected = to - from
          const nextLen = current.length - selected + text.length
          if (nextLen <= cap) return false
          const allowed = cap - (current.length - selected)
          if (allowed <= 0) return true
          const slice = [...text].slice(0, allowed).join('')
          if (!slice) return true
          const { tr } = view.state
          view.dispatch(tr.insertText(slice, from, to))
          return true
        },
        handleKeyDown: (view, event) => {
          const nav = rowNavRef.current
          if (nav && singleLine) {
            if (event.key === 'Enter' && !event.shiftKey && selectionAtEnd(view)) {
              event.preventDefault()
              if (editorRef.current) flushSave(editorRef.current)
              nav.onEnterEnd()
              return true
            }
            if (
              event.key === 'Backspace' &&
              selectionAtStart(view) &&
              nav.isEmpty()
            ) {
              event.preventDefault()
              nav.onBackspaceEmpty()
              return true
            }
            if (event.key === 'ArrowDown' && selectionAtEnd(view)) {
              event.preventDefault()
              nav.onArrowDown()
              return true
            }
            if (event.key === 'ArrowUp' && selectionAtStart(view)) {
              event.preventDefault()
              nav.onArrowUp()
              return true
            }
          }

          if (
            event.key === 'Backspace' &&
            selectionAtStart(view) &&
            onBackspaceWhenEmpty &&
            !view.state.doc.textContent.trim()
          ) {
            event.preventDefault()
            onBackspaceWhenEmpty()
            return true
          }

          if (singleLine && event.key === 'Enter') {
            event.preventDefault()
            return true
          }
          return false
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (composingRef.current) return
        const cap = maxLengthRef.current
        if (cap && !richText && ed.getText().length > cap) {
          const clipped = clampPlainText(ed.getText(), cap)
          ed.commands.setContent(toInitialHtml(clipped, false), { emitUpdate: false })
          debouncedSave(clipped)
          return
        }
        debouncedSave(richText ? ed.getHTML() : ed.getText())
      },
    },
    [instanceKey, sessionEpoch],
  )

  editorRef.current = editor

  const [viewReady, setViewReady] = useState(false)

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      setViewReady(false)
      return undefined
    }

    const syncViewReady = () => {
      setViewReady(Boolean(getSafeEditorView(editor)))
    }

    const onDestroy = () => setViewReady(false)

    syncViewReady()
    editor.on('create', syncViewReady)
    editor.on('destroy', onDestroy)

    const frame = requestAnimationFrame(syncViewReady)

    return () => {
      cancelAnimationFrame(frame)
      editor.off('create', syncViewReady)
      editor.off('destroy', onDestroy)
      setViewReady(false)
    }
  }, [editor, instanceKey, sessionEpoch])

  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed || !viewReady) return undefined
    const node = getSafeEditorDom(editor)
    if (!(node instanceof HTMLElement)) return undefined
    const wrap = fieldRef.current
    const isInlineField =
      inline || wrap?.hasAttribute('data-nb-inline') === true
    const isFlexRowField = singleLine && !isInlineField
    if (isInlineField || isFlexRowField) {
      node.style.width = 'auto'
      node.style.minWidth = '0'
      node.style.maxWidth = '100%'
      if (wrap instanceof HTMLElement) {
        wrap.style.width = 'auto'
        wrap.style.minWidth = isFlexRowField ? '2.5ch' : '0'
        wrap.style.maxWidth = '100%'
      }
    } else {
      node.style.width = '100%'
      node.style.minWidth = '0'
      node.style.maxWidth = '100%'
      if (wrap instanceof HTMLElement) {
        wrap.style.width = '100%'
        wrap.style.minWidth = '0'
      }
    }
    void node.offsetWidth
    return undefined
  }, [editor, viewReady, instanceKey, sessionEpoch, inline, singleLine])

  useLayoutEffect(() => {
    if (!editor || editor.isDestroyed) return undefined
    return registerNotebookEditor(editor)
  }, [editor])

  useLayoutEffect(() => {
    if (!autoFocus || !editor || editor.isDestroyed || !viewReady) return undefined
    const frame = requestAnimationFrame(() => {
      if (!editor.isDestroyed) {
        editor.commands.focus('end')
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [autoFocus, editor, viewReady])

  useEffect(() => {
    return () => {
      debouncedSave.cancel()
      if (editor && !editor.isDestroyed) {
        try {
          editor.destroy()
        } catch {
          /* ignore teardown races */
        }
      }
    }
  }, [editor, debouncedSave])

  useEffect(() => {
    const node = getSafeEditorDom(editor)
    if (!node) return undefined
    if (node.dataset.editorOwned === 'true') {
      nbWarn('EditorView mounted on already-owned DOM node', {
        instanceKey,
        sessionEpoch,
        node,
        scan: scanNotebookDom(),
      })
    }
    node.dataset.editorOwned = 'true'
    nbLog('editor', 'EditorView DOM attached', {
      instanceKey,
      sessionEpoch,
      preview: (node.textContent ?? '').slice(0, 40),
      rect: node.getBoundingClientRect?.()
        ? {
            top: Math.round(node.getBoundingClientRect().top),
            left: Math.round(node.getBoundingClientRect().left),
          }
        : null,
    })
    return () => {
      node.dataset.editorOwned = 'false'
    }
  }, [editor, instanceKey, sessionEpoch])

  useNotebookBubbleMenuDismiss(editor, toolbarRootRef, fieldRef)

  useEffect(() => {
    if (!editor) return
    editor.setEditable(!locked)
  }, [editor, locked])

  useEffect(() => {
    if (!editor || editor.isDestroyed) return
    if (
      editor.isFocused ||
      composingRef.current ||
      editor.view?.hasFocus?.()
    ) {
      return
    }

    const external = value ?? ''
    const current = plainTextFromEditor(editor, richText)
    if (current === external) return

    editor.commands.setContent(toInitialHtml(external, richText), {
      emitUpdate: false,
    })
  }, [editor, value, richText])

  const onCompositionStart = useCallback(() => {
    composingRef.current = true
  }, [])

  const onCompositionEnd = useCallback(() => {
    composingRef.current = false
    if (editor) flushSave(editor)
  }, [editor, flushSave])

  const onPasteBubbleStop = useCallback(
    (e) => {
      if (isolatePasteBubble) e.stopPropagation()
    },
    [isolatePasteBubble],
  )

  const onRowFocusIn = useCallback(() => {
    registerFocus()
  }, [registerFocus])

  const onRowFocusOut = useCallback(() => {
    if (!isSemanticRow) return
    window.requestAnimationFrame(() => {
      const active = document.activeElement
      if (active instanceof Element && active.closest('[data-nb-semantic-row]')) {
        return
      }
      clearRowFocusMeta()
    })
  }, [isSemanticRow])

  const onFieldBlur = useCallback(() => {
    window.requestAnimationFrame(() => {
      const active = document.activeElement
      if (fieldRef.current?.contains(active)) return
      if (toolbarRootRef.current?.contains(active)) return
      if (
        active instanceof Element &&
        active.closest('[data-nb-tiptap-field], [data-nb-tiptap-static]')
      ) {
        return
      }
      if (editor && !editor.isDestroyed) {
        flushSave(editor)
      }
      onDeactivate?.(instanceKey)
    })
  }, [editor, flushSave, onDeactivate, instanceKey])

  const isInline = Boolean(inline)
  const isFlexRowField = singleLine && !isInline
  const fieldStyle = isInline
    ? { minWidth: 0, width: 'auto', maxWidth: '100%', ...style }
    : isFlexRowField
      ? {
          flex: '1 1 auto',
          minWidth: '2.5ch',
          width: 'auto',
          maxWidth: '100%',
          ...style,
        }
      : { width: '100%', minWidth: 0, maxWidth: '100%', ...style }

  if (!editor || editor.isDestroyed || !viewReady) {
    const bootRaw = (value ?? '').replace(/<[^>]+>/g, '').trim()
    const bootText = clampPlainText(bootRaw, maxLength) || '\u00a0'
    return (
      <div
        ref={fieldRef}
        className={cn('nb-tiptap-field nb-tiptap-field--boot', className)}
        style={fieldStyle}
        data-nb-tiptap-field
        data-nb-inline={isInline ? '' : undefined}
        data-single-line={singleLine || undefined}
      >
        <span className="nb-tiptap-field__boot">{bootText}</span>
      </div>
    )
  }

  return (
    <div
      ref={fieldRef}
      className={cn('nb-tiptap-field', className)}
      style={fieldStyle}
      data-nb-tiptap-field
      data-nb-inline={isInline ? '' : undefined}
      data-single-line={singleLine || undefined}
      data-nb-semantic-row={isSemanticRow ? '' : undefined}
      data-nb-row-id={isSemanticRow ? rowId : undefined}
      data-nb-row-index={isSemanticRow && rowIndex != null ? String(rowIndex) : undefined}
      data-nb-row-field={isSemanticRow ? rowField : undefined}
      onCompositionStart={onCompositionStart}
      onCompositionEnd={onCompositionEnd}
      onPaste={onPasteBubbleStop}
      onFocusCapture={isSemanticRow ? onRowFocusIn : undefined}
      onBlurCapture={(event) => {
        if (isSemanticRow) onRowFocusOut()
        onFieldBlur(event)
      }}
    >
      <EditorContent editor={editor} />
      {bubbleToolbar ? (
        <BubbleMenu
          editor={editor}
          tippyOptions={{
            appendTo: () => fieldRef.current ?? document.body,
          }}
          shouldShow={({ editor: ed, state }) => {
            const { from, to } = state.selection
            if (from === to || !ed.isEditable) return false
            if (
              !ed.isFocused &&
              !isFocusInBubbleToolbar(toolbarRootRef.current)
            ) {
              return false
            }
            return true
          }}
        >
          <div ref={toolbarRootRef} data-nb-bubble-toolbar>
            <NotebookBubbleToolbar editor={editor} />
          </div>
        </BubbleMenu>
      ) : null}
    </div>
  )
}

/** Live Tiptap field — direct render (no editor gate). */
export function NotebookTiptapField(props) {
  return <NotebookTiptapFieldLive {...props} />
}
